import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';

export type EnergyScaleType = 'energy' | 'ghg';

@Component({
  selector: 'etxe-energy-class-selector',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './energy-class-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnergyClassSelectorComponent {
  value = model<string | null | undefined>();
  type = input<EnergyScaleType>('energy');
  readonly = input<boolean>(false);

  grades = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

  ranges = computed<Record<string, string>>(() => {
    if (this.type() === 'energy') {
      return {
        A: '≤ 70',
        B: '71 - 110',
        C: '111 - 180',
        D: '181 - 250',
        E: '251 - 330',
        F: '331 - 420',
        G: '> 420',
      };
    }
    return {
      A: '≤ 6',
      B: '7 - 11',
      C: '12 - 30',
      D: '31 - 50',
      E: '51 - 70',
      F: '71 - 100',
      G: '> 100',
    };
  });

  description = computed(() => {
    if (this.type() === 'energy') {
      return { low: 'Logement économe', high: 'Logement énergivore' };
    }
    return { low: "Peu d'émissions", high: 'Fortes émissions' };
  });

  // Official Spectrum Colors (Standardized)
  private energyColors: Record<string, string> = {
    A: '#00803C', // Dark Green
    B: '#54B345', // Green
    C: '#CED932', // Lime
    D: '#FFF200', // Yellow
    E: '#FCBA08', // Orange
    F: '#EB8235', // Dark Orange
    G: '#D71D20', // Red
  };

  private ghgColors: Record<string, string> = {
    A: '#ECF7FB', // Light Blue
    B: '#D1E8F2',
    C: '#A7CFE2',
    D: '#7DB7D2',
    E: '#529EC1',
    F: '#2885B1',
    G: '#006DA1', // Dark Blue
  };

  getGradeColor(grade: string): string {
    return this.type() === 'energy' ? this.energyColors[grade] : this.ghgColors[grade];
  }

  getBackgroundColor(grade: string): string {
    return this.isSelected(grade) ? this.getGradeColor(grade) : 'var(--color-neutral-2)';
  }

  /**
   * Primary text color logic using strict contrast rules.
   */
  getTextColor(grade: string): string {
    if (this.isSelected(grade)) {
      if (this.type() === 'energy') {
        // A, B, F, G (Darker) -> White | C, D, E (Lighter) -> Black
        return ['A', 'B', 'F', 'G'].includes(grade) ? '#FFFFFF' : '#000000';
      } else {
        // GHG: A, B, C (Light) -> Black | D, E, F, G (Darker) -> White
        return ['A', 'B', 'C'].includes(grade) ? '#000000' : '#FFFFFF';
      }
    }
    return 'var(--color-neutral-12)'; // High contrast neutral for unselected
  }

  /**
   * Returns a sharp 1px outline using text-shadow.
   * This ensures that even if a color is borderline, the letter stands out.
   */
  getTextOutline(grade: string): string {
    if (!this.isSelected(grade)) return 'none';

    // Use the opposite color for the outline
    const color = this.getTextColor(grade) === '#FFFFFF' ? '#000000' : '#FFFFFF';

    // Create a sharp 1px outline using 4 shadows (top-left, top-right, bottom-left, bottom-right)
    return `
      -1px -1px 0 ${color},  
       1px -1px 0 ${color},
      -1px  1px 0 ${color},
       1px  1px 0 ${color}
    `.trim();
  }

  isSelected(grade: string): boolean {
    return this.value() === grade;
  }

  select(grade: string) {
    this.value.set(grade);
  }
}
